import React, { useState, useEffect, useCallback } from 'react';
import { XMLTesterService, XMLOperation } from '../../lib/services/xmlTesterService';
import { EncodingOperation } from '../../lib/types/EncodingOperation';
import './XMLTester.css';

export const XMLTester: React.FC = () => {
  const [xmlInput, setXmlInput] = useState<string>(XMLTesterService.getSampleXML());
  const [xpathQuery, setXpathQuery] = useState<string>('//book/title/text()');
  const [xsdSchema, setXsdSchema] = useState<string>('');
  const [activeOperation, setActiveOperation] = useState<XMLOperation>('xpath');
  const [result, setResult] = useState<EncodingOperation | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCheatSheet, setShowCheatSheet] = useState(false);

  const processOperation = useCallback(async () => {
    setIsProcessing(true);
    try {
      let operation: EncodingOperation;

      switch (activeOperation) {
        case 'xpath':
          if (xpathQuery) {
            operation = await XMLTesterService.executeXPath(xmlInput, xpathQuery);
          } else {
            setIsProcessing(false);
            return;
          }
          break;
        case 'validate':
          operation = await XMLTesterService.validate(xmlInput, xsdSchema || undefined);
          break;
        case 'format':
          operation = await XMLTesterService.format(xmlInput);
          break;
        case 'minify':
          operation = await XMLTesterService.minify(xmlInput);
          break;
        default:
          setIsProcessing(false);
          return;
      }

      setResult(operation);
    } catch (error) {
      console.error('XML processing error:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [xmlInput, xpathQuery, xsdSchema, activeOperation]);

  // Process operation with debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (xmlInput) {
        processOperation();
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [xmlInput, processOperation]);

  const handleExampleClick = (query: string) => {
    setXpathQuery(query);
    if (activeOperation !== 'xpath') {
      setActiveOperation('xpath');
    }
  };

  const handleClear = () => {
    setXmlInput('');
    setXpathQuery('');
    setXsdSchema('');
    setResult(null);
  };

  const handleLoadSample = () => {
    setXmlInput(XMLTesterService.getSampleXML());
    setXpathQuery('//book/title/text()');
  };

  const handleLoadSampleXSD = () => {
    setXsdSchema(XMLTesterService.getSampleXSD());
  };

  const handleCopyResult = async () => {
    if (result?.outputData && typeof result.outputData === 'string') {
      try {
        await navigator.clipboard.writeText(result.outputData);
      } catch (error) {
        console.error('Failed to copy:', error);
      }
    }
  };

  const handleOperationChange = (operation: XMLOperation) => {
    setActiveOperation(operation);
    setResult(null);
  };

  const examples = XMLTesterService.getExampleXPaths();

  // Get formatted result for display
  const getDisplayResult = () => {
    if (!result) return '';
    if (result.status === 'error') return result.errorMessage || 'An error occurred';

    if (activeOperation === 'validate') {
      try {
        const validationResult = JSON.parse(result.outputData as string);
        if (validationResult.isValid) {
          return '✓ XML is valid';
        } else {
          return `✗ XML has ${validationResult.errors.length} error(s):\n\n${
            validationResult.errors.map((err: any, idx: number) =>
              `${idx + 1}. ${err.message}`
            ).join('\n')
          }`;
        }
      } catch (error) {
        return result.outputData as string;
      }
    }

    return result.outputData as string;
  };

  return (
    <div className="xml-tester">
      <h1>XML Tester</h1>
      <p className="description">
        Test XPath queries, validate XML, and format/minify XML documents.
      </p>

      <div className="xml-container">
        <div className="input-section">
          <div className="input-group">
            <label htmlFor="xml-input">
              XML Data
              <button
                className="btn-secondary btn-small"
                onClick={handleLoadSample}
                type="button"
                aria-label="Load sample XML"
              >
                Load Sample
              </button>
            </label>
            <textarea
              id="xml-input"
              value={xmlInput}
              onChange={(e) => setXmlInput(e.target.value)}
              placeholder="Enter XML data..."
              rows={12}
              spellCheck={false}
              aria-label="XML input data"
            />
          </div>

          <div className="operations-tabs" role="tablist" aria-label="XML operations">
            <button
              className={`tab-button ${activeOperation === 'xpath' ? 'active' : ''}`}
              onClick={() => handleOperationChange('xpath')}
              type="button"
              role="tab"
              aria-selected={activeOperation === 'xpath'}
              aria-controls="operation-panel"
            >
              XPath
            </button>
            <button
              className={`tab-button ${activeOperation === 'validate' ? 'active' : ''}`}
              onClick={() => handleOperationChange('validate')}
              type="button"
              role="tab"
              aria-selected={activeOperation === 'validate'}
              aria-controls="operation-panel"
            >
              Validate
            </button>
            <button
              className={`tab-button ${activeOperation === 'format' ? 'active' : ''}`}
              onClick={() => handleOperationChange('format')}
              type="button"
              role="tab"
              aria-selected={activeOperation === 'format'}
              aria-controls="operation-panel"
            >
              Format
            </button>
            <button
              className={`tab-button ${activeOperation === 'minify' ? 'active' : ''}`}
              onClick={() => handleOperationChange('minify')}
              type="button"
              role="tab"
              aria-selected={activeOperation === 'minify'}
              aria-controls="operation-panel"
            >
              Minify
            </button>
          </div>

          <div id="operation-panel" role="tabpanel">
            {activeOperation === 'xpath' && (
              <div className="input-group">
                <label htmlFor="xpath-query">
                  XPath Query
                  <button
                    className="btn-secondary btn-small"
                    onClick={() => setShowCheatSheet(!showCheatSheet)}
                    type="button"
                    aria-label="Toggle cheat sheet"
                    aria-expanded={showCheatSheet}
                  >
                    {showCheatSheet ? 'Hide' : 'Show'} Cheat Sheet
                  </button>
                </label>
                <input
                  type="text"
                  id="xpath-query"
                  value={xpathQuery}
                  onChange={(e) => setXpathQuery(e.target.value)}
                  placeholder="Enter XPath query (e.g., //book/title)"
                  spellCheck={false}
                  aria-label="XPath query"
                />
              </div>
            )}

            {activeOperation === 'validate' && (
              <div className="input-group xsd-group">
                <label htmlFor="xsd-schema">
                  XSD Schema (Optional)
                  <button
                    className="btn-secondary btn-small"
                    onClick={handleLoadSampleXSD}
                    type="button"
                    aria-label="Load sample XSD schema"
                  >
                    Load Sample XSD
                  </button>
                </label>
                <textarea
                  id="xsd-schema"
                  value={xsdSchema}
                  onChange={(e) => setXsdSchema(e.target.value)}
                  placeholder="Enter XSD schema to validate against (optional)..."
                  rows={8}
                  spellCheck={false}
                  aria-label="XSD schema for validation"
                />
                <p className="input-hint">
                  Leave empty for basic XML structure validation, or provide an XSD schema for detailed validation.
                </p>
              </div>
            )}

            {activeOperation === 'format' && (
              <div className="operation-info">
                <p>Your XML will be formatted with proper indentation for readability.</p>
              </div>
            )}

            {activeOperation === 'minify' && (
              <div className="operation-info">
                <p>Your XML will be minified by removing unnecessary whitespace.</p>
              </div>
            )}
          </div>

          <div className="button-group">
            <button
              className="btn-secondary"
              onClick={handleClear}
              type="button"
              aria-label="Clear all inputs"
            >
              Clear
            </button>
          </div>
        </div>

        {showCheatSheet && activeOperation === 'xpath' && (
          <div className="cheat-sheet" role="region" aria-label="XPath cheat sheet">
            <h3>XPath Cheat Sheet</h3>
            <div className="examples-grid">
              {examples.map((example) => (
                <div key={example.name} className="example-item">
                  <button
                    className="example-button"
                    onClick={() => handleExampleClick(example.query)}
                    type="button"
                    aria-label={`Use example: ${example.name}`}
                  >
                    <div className="example-name">{example.name}</div>
                    <code className="example-query">{example.query}</code>
                    <div className="example-description">{example.description}</div>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="output-section">
          <div className="output-header">
            <label htmlFor="xml-result">Result</label>
            <div className="output-actions">
              {isProcessing && (
                <span className="processing-badge" aria-live="polite">
                  Processing...
                </span>
              )}
              {result?.status === 'success' && activeOperation === 'xpath' && (
                <span className="match-count" aria-live="polite">
                  {result.metadata?.resultCount || 0} result{result.metadata?.resultCount !== 1 ? 's' : ''}
                </span>
              )}
              {result?.status === 'success' && activeOperation === 'validate' && result.metadata?.isValid && (
                <span className="success-badge" aria-live="polite">
                  Valid XML
                </span>
              )}
              {result?.status === 'success' && activeOperation === 'validate' && !result.metadata?.isValid && (
                <span className="error-badge" aria-live="polite">
                  {result.metadata?.errorCount} error(s)
                </span>
              )}
              <button
                className="btn-secondary btn-small"
                onClick={handleCopyResult}
                disabled={!result?.outputData}
                type="button"
                aria-label="Copy result to clipboard"
              >
                Copy
              </button>
            </div>
          </div>

          <textarea
            id="xml-result"
            value={getDisplayResult()}
            readOnly
            rows={12}
            placeholder="Results will appear here..."
            className={result?.status === 'error' || (result?.metadata?.isValid === false) ? 'error' : ''}
            spellCheck={false}
            aria-label="XML operation result"
            aria-live="polite"
          />

          {result?.metadata && (
            <div className="metadata" aria-label="Operation metadata">
              <span>Operation: <code>{result.metadata.operation}</code></span>
              {result.metadata.query && <span>Query: <code>{result.metadata.query}</code></span>}
              {result.metadata.resultCount !== undefined && <span>Results: {result.metadata.resultCount}</span>}
              {result.metadata.errorCount !== undefined && <span>Errors: {result.metadata.errorCount}</span>}
              {result.metadata.hasXSD && <span>Validation: <code>XSD</code></span>}
              <span>Input: {result.metadata.inputLength} chars</span>
              {result.metadata.outputLength !== undefined && <span>Output: {result.metadata.outputLength} chars</span>}
              {result.metadata.compressionRatio && <span>Saved: {result.metadata.compressionRatio}</span>}
            </div>
          )}
        </div>
      </div>

      <noscript>
        <div className="noscript-warning">
          This tool requires JavaScript to be enabled for interactive XML testing.
        </div>
      </noscript>
    </div>
  );
};
